import React, { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { db } from './firebase';
import { collection, onSnapshot, query, orderBy } from './firestore-compat';

// Fix ícones do Leaflet com Create React App
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Paleta de cores para grupos
const CORE_COLORS = ['#10b981','#6366f1','#f59e0b','#ef4444','#8b5cf6','#06b6d4','#ec4899','#f97316','#84cc16','#14b8a6'];

// Ícone personalizado por cor
function createColoredIcon(color) {
  return L.divIcon({
    className: '',
    html: `<div style="
      width: 22px; height: 22px;
      background: ${color};
      border: 3px solid white;
      border-radius: 50%;
      box-shadow: 0 2px 8px rgba(0,0,0,0.4);
    "></div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
    popupAnchor: [0, -14],
  });
}

// Centraliza o mapa nos dados filtrados
function MapCenterUpdater({ pacientes }) {
  const map = useMap();
  useEffect(() => {
    if (pacientes.length > 0) {
      const bounds = L.latLngBounds(pacientes.map(p => [p._lat, p._lng]));
      if (bounds.isValid()) map.fitBounds(bounds, { padding: [40, 40], maxZoom: 13 });
    }
  }, [pacientes, map]);
  return null;
}

const MAPA_DESATIVADO = true;

export default function MapaPacientes() {
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtros
  const [filtros, setFiltros] = useState({});
  const [buscaTexto, setBuscaTexto] = useState('');
  const [colunaAgrupamento, setColunaAgrupamento] = useState('');

  // Carrega dados de geolocalização
  useEffect(() => {
    if (MAPA_DESATIVADO) { setLoading(false); return; }
    const q = query(collection(db, 'geo_pacientes'), orderBy('timestamp', 'desc'));
    return onSnapshot(q, snap => {
      setUploads(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
  }, []);

  // Pacientes do upload mais recente
  const { allPacientes, colunas, latCol, lngCol } = useMemo(() => {
    if (uploads.length === 0) return { allPacientes: [], colunas: [], latCol: null, lngCol: null };
    const latest = uploads[0];
    return {
      allPacientes: latest.pacientes || [],
      colunas: latest.colunas || [],
      latCol: latest.latCol,
      lngCol: latest.lngCol,
    };
  }, [uploads]);

  // Valores únicos por coluna
  const valoresPorColuna = useMemo(() => {
    const map = {};
    colunas.forEach(col => {
      if (col === latCol || col === lngCol) return;
      map[col] = [...new Set(allPacientes.map(p => p[col]).filter(v => v && v.trim()))].sort();
    });
    return map;
  }, [allPacientes, colunas, latCol, lngCol]);

  // Coluna padrão para agrupamento
  useEffect(() => {
    if (!colunaAgrupamento && colunas.length > 0) {
      const col = colunas.find(c => c !== latCol && c !== lngCol);
      if (col) setColunaAgrupamento(col);
    }
  }, [colunas, latCol, lngCol]);

  // Mapa de cores por valor do grupo
  const colorMap = useMemo(() => {
    if (!colunaAgrupamento) return {};
    const valores = [...new Set(allPacientes.map(p => p[colunaAgrupamento]).filter(Boolean))].sort();
    const map = {};
    valores.forEach((v, i) => { map[v] = CORE_COLORS[i % CORE_COLORS.length]; });
    return map;
  }, [allPacientes, colunaAgrupamento]);

  // Aplica filtros
  const filtered = useMemo(() => {
    let rows = allPacientes.filter(p => p._lat && p._lng);
    Object.entries(filtros).forEach(([col, val]) => {
      if (val && val !== 'all') rows = rows.filter(p => p[col] === val);
    });
    if (buscaTexto.trim()) {
      const t = buscaTexto.toLowerCase();
      rows = rows.filter(p => colunas.some(col => String(p[col] || '').toLowerCase().includes(t)));
    }
    return rows;
  }, [allPacientes, filtros, buscaTexto, colunas]);

  const centerDefault = filtered.length > 0
    ? [filtered[0]._lat, filtered[0]._lng]
    : [-15.77972, -47.92972]; // Brasília como fallback

  const handleFiltro = (col, val) => setFiltros(prev => ({ ...prev, [col]: val }));
  const limparFiltros = () => { setFiltros({}); setBuscaTexto(''); };
  const filtrosAtivos = Object.values(filtros).filter(v => v && v !== 'all').length + (buscaTexto ? 1 : 0);

  // ── Estilos ───────────────────────────────────────────────────────────────
  const s = {
    page: { minHeight: '100vh', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)', padding: '24px', fontFamily: "'Inter','Segoe UI',sans-serif", color: '#e2e8f0' },
    header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: 12 },
    headerLeft: { display: 'flex', alignItems: 'center', gap: 14 },
    headerIcon: { width: 48, height: 48, background: 'linear-gradient(135deg, #f59e0b, #d97706)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', boxShadow: '0 4px 20px rgba(245,158,11,0.4)' },
    title: { margin: 0, fontSize: '1.5rem', fontWeight: 800, color: '#f1f5f9' },
    subtitle: { margin: '3px 0 0', fontSize: '0.85rem', color: '#94a3b8' },
    card: { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', padding: '16px 20px', backdropFilter: 'blur(10px)' },
    select: { padding: '7px 10px', borderRadius: '8px', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: '#e2e8f0', fontSize: '0.82rem', cursor: 'pointer', outline: 'none' },
    input: { padding: '7px 10px', borderRadius: '8px', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: '#e2e8f0', fontSize: '0.82rem', outline: 'none', minWidth: 180 },
    btn: { padding: '7px 14px', borderRadius: '8px', border: 'none', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer', background: 'rgba(255,255,255,0.08)', color: '#fff', transition: 'all 0.2s' },
    legend: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' },
    legendItem: (color) => ({ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.78rem', color: '#cbd5e1' }),
    legendDot: (color) => ({ width: 12, height: 12, borderRadius: '50%', background: color, border: '2px solid rgba(255,255,255,0.5)', flexShrink: 0 }),
  };

  if (loading) return (
    <div style={{ ...s.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: 16 }}>🗺️</div>
        <div style={{ color: '#f59e0b', fontWeight: 700 }}>Carregando mapa...</div>
      </div>
    </div>
  );

  if (MAPA_DESATIVADO) return (
    <div style={{ padding: '80px 20px', textAlign: 'center' }}>
      <div style={{ fontSize: 54, marginBottom: 16 }}>🛠️</div>
      <h3 style={{ color: '#1e293b', marginBottom: 8, fontWeight: 700, fontSize: '1.2rem' }}>Mapa de Pacientes desativado</h3>
      <p style={{ color: '#64748b', fontSize: '0.9rem' }}>Este módulo não está mais em uso.</p>
    </div>
  );

  if (allPacientes.length === 0) return (
    <div style={s.page}>
      <div style={s.header}>
        <div style={s.headerLeft}>
          <div style={s.headerIcon}>🗺️</div>
          <div>
            <h1 style={s.title}>Mapa de Pacientes</h1>
            <p style={s.subtitle}>Nenhum dado de geolocalização carregado</p>
          </div>
        </div>
      </div>
      <div style={{ ...s.card, textAlign: 'center', padding: '60px' }}>
        <div style={{ fontSize: '4rem', marginBottom: 16 }}>📭</div>
        <div style={{ fontWeight: 700, color: '#94a3b8', marginBottom: 8 }}>Nenhum paciente cadastrado</div>
        <div style={{ color: '#64748b', fontSize: '0.88rem' }}>Acesse "Upload Geolocalização" para enviar a planilha com coordenadas.</div>
      </div>
    </div>
  );

  return (
    <div style={s.page}>
      {/* ── Cabeçalho ── */}
      <div style={s.header}>
        <div style={s.headerLeft}>
          <div style={s.headerIcon}>🗺️</div>
          <div>
            <h1 style={s.title}>Mapa de Pacientes</h1>
            <p style={s.subtitle}>
              {filtered.length.toLocaleString('pt-BR')} pacientes exibidos de {allPacientes.length.toLocaleString('pt-BR')} total
              {filtrosAtivos > 0 && <span style={{ color: '#f59e0b' }}> · {filtrosAtivos} filtro(s) ativo(s)</span>}
            </p>
          </div>
        </div>
        {filtrosAtivos > 0 && (
          <button style={s.btn} onClick={limparFiltros}>🗑️ Limpar Filtros</button>
        )}
      </div>

      {/* ── Filtros ── */}
      <div style={{ ...s.card, marginBottom: '16px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.7rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>Busca</label>
            <input style={s.input} placeholder="🔎 Buscar paciente..." value={buscaTexto} onChange={e => setBuscaTexto(e.target.value)} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.7rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>Agrupar por</label>
            <select style={s.select} value={colunaAgrupamento} onChange={e => setColunaAgrupamento(e.target.value)}>
              {colunas.filter(c => c !== latCol && c !== lngCol).map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          {colunas.filter(c => c !== latCol && c !== lngCol).map(col => {
            const vals = valoresPorColuna[col] || [];
            if (vals.length < 2 || vals.length > 80) return null;
            return (
              <div key={col}>
                <label style={{ display: 'block', fontSize: '0.7rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>{col}</label>
                <select style={s.select} value={filtros[col] || 'all'} onChange={e => handleFiltro(col, e.target.value)}>
                  <option value="all">Todos</option>
                  {vals.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
            );
          })}
        </div>

        {/* Legenda de cores */}
        {colunaAgrupamento && Object.entries(colorMap).length > 0 && (
          <div style={s.legend}>
            <span style={{ fontSize: '0.75rem', color: '#64748b', alignSelf: 'center', marginRight: '4px' }}>Legenda:</span>
            {Object.entries(colorMap).map(([val, color]) => (
              <div key={val} style={s.legendItem(color)}>
                <div style={s.legendDot(color)} />
                <span>{val}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── KPIs rápidos ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px,1fr))', gap: '12px', marginBottom: '16px' }}>
        {[
          { icon: '📍', label: 'No Mapa', value: filtered.length, color: '#f59e0b' },
          { icon: '👥', label: 'Total Cadastrado', value: allPacientes.length, color: '#6366f1' },
          { icon: '📅', label: 'Último Upload', value: uploads[0]?.dataReferencia || '--', color: '#10b981' },
        ].map((kpi, i) => (
          <div key={i} style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${kpi.color}30`, borderRadius: '12px', padding: '14px 16px' }}>
            <div style={{ fontSize: '1.2rem', marginBottom: '4px' }}>{kpi.icon}</div>
            <div style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '0.06em' }}>{kpi.label}</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: kpi.color }}>{typeof kpi.value === 'number' ? kpi.value.toLocaleString('pt-BR') : kpi.value}</div>
          </div>
        ))}
      </div>

      {/* ── Mapa ── */}
      <div style={{ borderRadius: '16px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)', height: '60vh', minHeight: 400 }}>
        <MapContainer center={centerDefault} zoom={8} style={{ width: '100%', height: '100%' }}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapCenterUpdater pacientes={filtered} />
          {filtered.map((p, i) => {
            const groupVal = colunaAgrupamento ? p[colunaAgrupamento] : null;
            const color = groupVal && colorMap[groupVal] ? colorMap[groupVal] : '#6366f1';
            const icon = createColoredIcon(color);
            const nomeCol = colunas.find(c => c.toLowerCase().includes('nome') || c.toLowerCase().includes('paciente'));
            const nome = nomeCol ? p[nomeCol] : `Paciente ${i + 1}`;

            return (
              <Marker key={i} position={[p._lat, p._lng]} icon={icon}>
                <Popup>
                  <div style={{ fontFamily: 'Inter, sans-serif', minWidth: 180 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: '8px', color: '#1e293b' }}>
                      📍 {nome}
                    </div>
                    <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '8px' }}>
                      {colunas.filter(c => c !== latCol && c !== lngCol && p[c]).slice(0, 8).map(col => (
                        <div key={col} style={{ fontSize: '0.8rem', color: '#475569', marginBottom: '3px' }}>
                          <strong>{col}:</strong> {p[col]}
                        </div>
                      ))}
                    </div>
                    <div style={{ marginTop: '8px', fontSize: '0.72rem', color: '#94a3b8', borderTop: '1px solid #e2e8f0', paddingTop: '6px' }}>
                      🌐 {p._lat?.toFixed(6)}, {p._lng?.toFixed(6)}
                    </div>
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      </div>

      {/* ── Lista rápida ── */}
      {filtered.length > 0 && (
        <div style={{ ...s.card, marginTop: '16px' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#f59e0b', marginBottom: '12px' }}>
            📋 Lista de Pacientes ({filtered.length})
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
              <thead>
                <tr>
                  {colunas.filter(c => c !== latCol && c !== lngCol).slice(0, 6).map(col => (
                    <th key={col} style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid rgba(255,255,255,0.08)', color: '#94a3b8', fontWeight: 600, fontSize: '0.72rem' }}>{col}</th>
                  ))}
                  <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid rgba(255,255,255,0.08)', color: '#94a3b8', fontWeight: 600, fontSize: '0.72rem' }}>Coordenadas</th>
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, 50).map((p, i) => {
                  const groupVal = colunaAgrupamento ? p[colunaAgrupamento] : null;
                  const color = groupVal && colorMap[groupVal] ? colorMap[groupVal] : '#6366f1';
                  return (
                    <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                      {colunas.filter(c => c !== latCol && c !== lngCol).slice(0, 6).map(col => (
                        <td key={col} style={{ padding: '8px 10px', borderBottom: '1px solid rgba(255,255,255,0.04)', color: col === colunaAgrupamento ? color : '#cbd5e1', fontWeight: col === colunaAgrupamento ? 700 : 400, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {p[col] || '—'}
                        </td>
                      ))}
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid rgba(255,255,255,0.04)', color: '#64748b', fontSize: '0.72rem' }}>
                        {p._lat?.toFixed(4)}, {p._lng?.toFixed(4)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filtered.length > 50 && (
              <div style={{ padding: '12px', fontSize: '0.8rem', color: '#64748b', textAlign: 'center' }}>
                Mostrando 50 de {filtered.length} pacientes. Use os filtros para refinar.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
